import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import "../../assets/css/AGGrid-custom.css";
import "../../assets/css/Text-General.css";

import { useReferenceLists } from "../../context/GlobalGetLists";
import { apiPut } from "../../services/genesisApi/httpsMethods";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomIconButton } from "../../components/common/customIconButton";
import { HyperLinkRenderer } from "../../utils/hyperlinkRenderer";
import pencilIcon from "@/assets/svg/edit.svg";
import downloadIcon from "@/assets/svg/downloads-dark.svg";
import type { Source } from "../../types/deal";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { numberFormatter } from "../../utils/numberFormatter";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

const Sources = () => {
  const { canEdit } = useCurrentUser();
  const gridRef = useRef<AgGridReact>(null);
  const [editMode, setEditMode] = useState(false);
  const [filter, setFilter] = useState("");
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [newSources, setNewSources] = useState<Source[]>([]);
  const [initialSources, setInitialSources] = useState<Source[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const { sources } = useReferenceLists();

  useEffect(() => {
    if (!sources) return;
    // Clone so grid edits never mutate the shared reference list.
    setNewSources(structuredClone(sources));
    setInitialSources(structuredClone(sources));
  }, [sources]);

  useEffect(() => {
    const isEqual = areRecordsEqual(initialSources, newSources);
    setDirtyGrid(!isEqual);
  }, [initialSources, newSources]);

  const onCellValueChanged = useCallback((event: any) => {
    const { colDef, newValue, oldValue, data } = event;
    if (newValue === oldValue) return;
    const field = colDef.field;
    setNewSources((prev) =>
      prev.map((s) => (s.id === data.id ? { ...s, [field]: newValue } : s)),
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    // Clone so AG Grid edits don't mutate the baseline rows.
    setNewSources(structuredClone(initialSources));
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    if (isSaving) return;
    const changedItems = getChangedRecords(newSources, initialSources);
    setIsSaving(true);

    const updates = changedItems.map((source) =>
      apiPut("source", source.id, {
        source_name: source.source_name,
        source_type: source.source_type ?? null,
      }).catch((error): ApiResponse => {
        console.error(`Failed to update source ${source.id}`, error);
        return { success: false, status: 0 };
      }),
    );

    const results = await Promise.all(updates);

    // Track any sources that fail to update so we can inform the user
    const failed = changedItems
      .map((source, index) => ({ source, response: results[index] }))
      .filter(({ response }) => !response?.success);

    if (failed.length > 0) {
      // Stay in edit mode and keep the user's edits so they can retry.
      setIsSaving(false);
      const names = failed
        .map(({ source }) => source.source_name || source.id)
        .join(", ");
      const firstResponse = failed[0].response;
      const rawDetail = firstResponse?.message;
      const detail =
        typeof rawDetail === "string" ? rawDetail : rawDetail?.message;
      showError(
        firstResponse,
        `Failed to update the following sources: ${names}.${
          detail ? ` ${detail}` : ""
        } Please review and try saving again.`,
      );
      return;
    }
    window.location.reload();
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const filteredRows = useMemo(() => {
    const needle = filter.toLowerCase();
    return newSources.filter((s) =>
      (s.source_name ?? "").toLowerCase().includes(needle),
    );
  }, [newSources, filter]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "source_name",
        headerName: "Source",
        editable: editMode,
        width: 300,
        cellRenderer: HyperLinkRenderer("sources"),
      },
      {
        field: "source_type",
        headerName: "Type",
        editable: editMode,
        width: 180,
      },
      { field: "deal_count", headerName: "Deals", editable: false, width: 90 },
      {
        field: "killed_deals",
        headerName: "Killed Deals",
        editable: false,
        width: 110,
      },
      {
        headerName: "Kill %",
        editable: false,
        width: 90,
        valueGetter: (params: any) => {
          const deals = Number(params?.data?.deal_count ?? 0);
          const killed = Number(params?.data?.killed_deals ?? 0);
          if (!deals) return null;
          return (killed / deals) * 100;
        },
        valueFormatter: (params: any) => {
          const v = params?.value;
          if (typeof v !== "number" || !isFinite(v)) return "-";
          return `${v.toFixed(0)}%`;
        },
      },
      {
        field: "total_loan_amount_fully_funded",
        headerName: "Total Loan Amount (GBP)",
        editable: false,
        width: 200,
        valueFormatter: numberFormatter,
        sort: "desc",
        sortIndex: 0,
      },
    ],
    [editMode],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <SimpleToolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              minWidth: 0,
            }}
          >
            <CustomTextInput
              label="Source name"
              value={filter}
              onChange={(val) => typeof val === "string" && setFilter(val)}
            />
          </Box>
          {editMode ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "15px" }}
              data-position="right"
            >
              <CustomButton
                text="Cancel"
                handleButtonPress={handleCancelButtonPress}
                isButtonDisabled={isSaving}
                solid={false}
                widthOverride="100px"
              />
              <CustomButton
                text="Save"
                handleButtonPress={requestSave}
                isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
                isLoading={isSaving}
                isLoadingText="Saving"
                solid
                widthOverride="100px"
              />
            </Box>
          ) : (
            <Box
              data-position="right"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: -0.5,
                "& .MuiIconButton-root": {
                  p: 0.6,
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  fontSize: 22,
                },
              }}
            >
              <CustomIconButton
                tooltip="Download"
                icon={
                  <Box
                    component="img"
                    src={downloadIcon}
                    alt="Download sources"
                    sx={{ width: 30, height: 30, display: "block" }}
                  />
                }
                iconButtonSx={{ p: 0 }}
                onClick={() =>
                  gridRef.current?.api.exportDataAsCsv({ fileName: "sources" })
                }
              />
              <CustomIconButton
                tooltip="Edit"
                icon={
                  <Box
                    component="img"
                    src={pencilIcon}
                    alt="Edit sources"
                    sx={{ width: 20, height: 20, display: "block" }}
                  />
                }
                disabled={!canEdit}
                onClick={() => {
                  if (!canEdit) return;
                  setEditMode(true);
                }}
              />
            </Box>
          )}
        </SimpleToolbar>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, p: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            {...gridEditProps}
            ref={gridRef}
            rowData={filteredRows}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default Sources;
